import type React from 'react';
import {useState} from 'react';
import {Avatar, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography} from '@mui/material';
import {MarkdownText, RichTextField} from '~/components/MarkdownEditor/MarkdownEditor';
import {LockOutlined} from '@mui/icons-material';
import {Tag} from '~/components/tag/Tag';
import type {RecordActions} from '../../hooks/useRecordActions';
import type {Note} from '../../types/categoryRecordTypes';
import {RowAction} from '../components/RowAction';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const REACTION_GLYPHS: Record<string, string> = {thumbs_up: '👍', heart: '❤️', celebrate: '🎉', eyes: '👀'};

const WHEN = new Intl.DateTimeFormat(LOCALE, {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'});

type Props = {
    note: Note;
    actions: RecordActions;
    canEdit: boolean;
    reactions: string[];
    /** Top-level notes get Reply; replies do not (one level of replies). */
    onReply?: () => void;
};

/** One note or reply: author, time, topic, status and Internal only; title and body; reactions and actions. */
export const NoteView: React.FC<Props> = ({note, actions, canEdit, reactions, onReply}) => {
    const isRoot = note.status !== null;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({title: '', body: '', internal: false});

    const startEdit = () => {
        setDraft({title: note.title ?? '', body: note.body, internal: note.internal});
        setEditing(true);
    };
    const save = () => {
        const change = isRoot ? draft : {body: draft.body};
        void actions.editNote(note, change).then(() => setEditing(false)).catch(() => undefined);
    };
    const keys = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') setEditing(false);
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) save();
    };
    const label = isRoot ? 'note' : 'reply';

    return (
        <Stack direction="row" gap={1.5} sx={{opacity: note.status === 'resolved' ? 0.75 : 1}}>
            <Avatar sx={{width: 28, height: 28, fontSize: 13}}>{note.author.name.slice(0, 1).toUpperCase()}</Avatar>
            <Box sx={{flex: 1, minWidth: 0}}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600}>{note.author.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {WHEN.format(new Date(note.createdAt))}{note.editedAt ? ' · edited' : ''}
                    </Typography>
                    {note.topic && <Tag label={note.topic} solid={false} color="default"/>}
                    {isRoot && <Tag label={note.status === 'resolved' ? 'Resolved' : 'Open'}
                                    color={note.status === 'resolved' ? 'success' : 'info'}/>}
                    {isRoot && note.internal && (
                        <Tag label="Internal only" color="warning" solid={false} icon={<LockOutlined sx={{fontSize: 14}}/>}
                             title="Hidden from read-only users"/>
                    )}
                </Stack>

                {editing ? (
                    <Stack gap={1} sx={{mt: 1}}>
                        {isRoot && (
                            <TextField size="small" placeholder="Title (optional)" value={draft.title} onKeyDown={keys}
                                       onChange={e => setDraft({...draft, title: e.target.value})}
                                       slotProps={{htmlInput: {maxLength: 200, 'aria-label': 'Note title'}}}/>
                        )}
                        <RichTextField initialMarkdown={note.body} autoFocus ariaLabel={isRoot ? 'Note' : 'Reply'}
                                       onChange={body => setDraft(d => ({...d, body}))} onSubmit={save}
                                       onCancel={() => setEditing(false)}/>
                        <Stack direction="row" gap={1} alignItems="center">
                            {isRoot && (
                                <FormControlLabel label="Internal only" sx={{mr: 1}}
                                                  control={<Checkbox size="small" checked={draft.internal}
                                                                     onChange={e => setDraft({...draft, internal: e.target.checked})}/>}/>
                            )}
                            <Button size="small" variant="contained" onClick={save} disabled={!draft.body.trim()}>Save</Button>
                            <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
                        </Stack>
                    </Stack>
                ) : (
                    <>
                        {note.title && <Typography variant="subtitle2" sx={{mt: 0.5}}>{note.title}</Typography>}
                        <Box sx={{mt: note.title ? 0.25 : 0.5}}><MarkdownText markdown={note.body}/></Box>
                    </>
                )}

                <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap" sx={{mt: 0.5}}>
                    {reactions.map(r => {
                        const given = note.reactions.find(x => x.reaction === r);
                        if (!canEdit && !given) return null;
                        return (
                            <Button key={r} size="small" variant={given?.mine ? 'outlined' : 'text'} disabled={!canEdit}
                                    aria-label={`React ${r.replace('_', ' ')}`} aria-pressed={Boolean(given?.mine)}
                                    onClick={() => void actions.toggleReaction(note, r).catch(() => undefined)}
                                    sx={{minWidth: 0, px: 0.75, py: 0}}>
                                {REACTION_GLYPHS[r]}{given ? ` ${given.count}` : ''}
                            </Button>
                        );
                    })}
                    {canEdit && onReply && <Button size="small" onClick={onReply}>Reply</Button>}
                    {canEdit && isRoot && (
                        <Button size="small" onClick={() => void actions.setNoteStatus(note,
                            note.status === 'resolved' ? 'open' : 'resolved').catch(() => undefined)}>
                            {note.status === 'resolved' ? 'Reopen' : 'Resolve'}
                        </Button>
                    )}
                    {canEdit && note.mine && !editing && <RowAction kind="edit" label={label} onClick={startEdit}/>}
                    {canEdit && note.mine && (
                        <RowAction kind="delete" label={label} onClick={() => void actions.removeNote(note).catch(() => undefined)}/>
                    )}
                </Stack>
            </Box>
        </Stack>
    );
};
